import { ManeuverSign } from './maneuverSign';
import { ManeuverOrigin, RouteManeuver } from './plannerInstructionParser';

/**
 * The directions list, worded.
 *
 * The wording rules are a port of `ManeuverPhrasing` on Android and iOS, and they matter more
 * than they look. The planner never asks GraphHopper for a locale, so its stored sentence is
 * always English; it cannot be inverted when a route is reversed; and a turn we synthesise
 * ourselves has no sentence at all. Composing from the sign and the path name solves all
 * three — which is why the stored text is used only when it is a person's own words, or when
 * the sign is one we cannot phrase (a roundabout carries an exit number nobody captured).
 */

export interface DirectionRow {
  index: number;
  sign: number;
  /** "Turn right onto Pier Road", or a person's own words verbatim. */
  instruction: string;
  /** The walk from the previous turn to this one — what a list is scanned by. */
  legDistanceMeters: number;
  legDistance: string;
  /** How far into the route this turn sits, for the running total beside each row. */
  distanceFromStartMeters: number;
  /** True for a turn nobody routed: a hand-added prompt of either kind. */
  isCue: boolean;
  /**
   * Which kind of prompt, when it is one — `"turn"` or `"note"`.
   *
   * Carried rather than inferred from the sign, because "General info" and "Continue
   * straight" are both sign 0. The kind is the only thing that tells them apart.
   */
  cueKind?: string;
  /** True for the closing arrival, which is drawn rather than worded like a turn. */
  isDestination: boolean;
  /**
   * The stored entry behind this row, when there is one. A seam and the closing arrival are
   * synthesised by the runtime and exist nowhere in storage, so they carry none — which is
   * what makes them uneditable without a separate rule to keep in step.
   */
  origin?: ManeuverOrigin;
}

const PHRASES: Record<number, string> = {
  [ManeuverSign.U_TURN_LEFT]: 'Turn around',
  [ManeuverSign.U_TURN_RIGHT]: 'Turn around',
  [ManeuverSign.KEEP_LEFT]: 'Keep left',
  [ManeuverSign.KEEP_RIGHT]: 'Keep right',
  [ManeuverSign.TURN_SHARP_LEFT]: 'Sharp left',
  [ManeuverSign.TURN_LEFT]: 'Turn left',
  [ManeuverSign.TURN_SLIGHT_LEFT]: 'Bear left',
  [ManeuverSign.CONTINUE]: 'Continue ahead',
  [ManeuverSign.TURN_SLIGHT_RIGHT]: 'Bear right',
  [ManeuverSign.TURN_RIGHT]: 'Turn right',
  [ManeuverSign.TURN_SHARP_RIGHT]: 'Sharp right'
};

/** What one maneuver should say. */
export function wordingFor(maneuver: RouteManeuver): string {
  const street = maneuver.streetName?.trim() || undefined;
  const stored = maneuver.text.trim();

  // The whole reason the stored text is normally discarded is that it is untranslated,
  // un-invertible and machine-written. None of that is true of a sentence a person typed,
  // so theirs is the one wording that must never be recomposed.
  if (maneuver.isUserWorded && stored !== '') return stored;

  const phrase = PHRASES[maneuver.sign];
  if (phrase === undefined) return stored !== '' ? stored : 'Continue ahead';

  if (street === undefined) return phrase;
  // A continue reads better without "ahead" once it names a path.
  if (maneuver.sign === ManeuverSign.CONTINUE) return `Continue onto ${street}`;
  return `${phrase} onto ${street}`;
}

/**
 * Distance as a directions list should say it.
 *
 * Whole metres below a kilometre — nobody paces out a tenth of a metre — and one decimal
 * above, with a trailing zero dropped. Matches `NavigationDistance` on the phones.
 */
export function formatLegDistance(meters: number): string {
  if (meters >= 1000) {
    const km = Math.round((meters / 1000) * 10) / 10;
    return `${Number.isInteger(km) ? km.toFixed(0) : km.toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

/**
 * Every turn on the route, in walking order.
 *
 * The leg distance is the walk *to* this turn from the one before, because that is how a
 * route card is read — "1.2 km, then turn left" — rather than distance from the trailhead.
 */
export function buildDirectionRows(maneuvers: RouteManeuver[]): DirectionRow[] {
  return maneuvers.map((maneuver, index) => {
    const previous = index > 0 ? maneuvers[index - 1].distanceFromStartMeters : 0;
    // Never negative: maneuvers arrive sorted, but a route that somehow carried an
    // out-of-order turn should read as zero rather than as a walk backwards.
    const leg = Math.max(0, maneuver.distanceFromStartMeters - previous);

    return {
      index,
      sign: maneuver.sign,
      instruction: wordingFor(maneuver),
      legDistanceMeters: leg,
      legDistance: formatLegDistance(leg),
      distanceFromStartMeters: maneuver.distanceFromStartMeters,
      isCue: maneuver.cue !== undefined,
      cueKind: maneuver.cue,
      isDestination: maneuver.sign === ManeuverSign.FINISH,
      origin: maneuver.origin
    };
  });
}
